/** Disable global custom elements registry to force MFEs to correctly implement
 *  Nectary and prevent conflicts.
 */
function preventUndefinedCustomElements() {
  customElements.define = () => {
    console.warn(
      "Someone tried to define a custom element globally! This is not allowed"
    );
  };
}

async function setupComponents(customElementRegistry: CustomElementRegistry) {
  const { setNectaryRegistry } = await import("@nectary/components/utils");
  setNectaryRegistry(customElementRegistry);
}

async function setupAssets(customElementRegistry: CustomElementRegistry) {
  const { setAssetsRegistry } = await import("@nectary/assets/utils");
  setAssetsRegistry(customElementRegistry);
}

/**
 * Sets up Nectary and its assets for use in either a shadow root or the global registry.
 *
 * @param {boolean} global - A flag indicating whether to use the global registry.
 *                          If false, a new CustomElementRegistry will be created.
 *
 * This function imports necessary modules and registers them with the appropriate
 * CustomElementRegistry, either the global registry or a new one.
 *
 * Usage:
 * - Call setupNectary(true) to setup Nectary in the global registry.
 * - Call setupNectary(false) to setup Nectary in a shadow root.
 *
 * Returns the CustomElementRegistry used for Nectary.
 */
export async function setupNectary(
  global: boolean
): Promise<CustomElementRegistry> {
  let registry: CustomElementRegistry;
  if (global) {
    registry = window.customElements;
  } else {
    preventUndefinedCustomElements();
    registry = new CustomElementRegistry();
  }

  await Promise.all([setupAssets(registry), setupComponents(registry)]);

  return registry;
}
